import { useEffect, useMemo, useState } from "react";
import { useIsUserNoticeActive } from "../hooks/useIsUserNoticeActive";
import type { LiveFlag } from "../hooks/useLiveFlags";
import {
  acknowledgeUserNotice,
  isUserNoticeAcknowledged,
  parseUserNotice
} from "../utils/userNotice";
import { UserNoticeBanner } from "./UserNoticeBanner";
import { UserNoticeModal } from "./modals/UserNoticeModal";

/**
 * Announces a message set by ops through the `user_notice` Flagsmith flag
 * (maintenance windows, incidents, anything users should read). The flag
 * value carries the text, the presentation (`type`: modal or banner) and an
 * optional display window (`startDateTime`/`endDateTime`), see
 * parseUserNotice. The notice shows only while the flag is enabled AND now is
 * inside the window, so ops can publish ahead of time and leave the flag on
 * afterwards without the notice lingering.
 *
 * Shown once per notice id: acknowledging records the id in localStorage and
 * the notice stays away until ops publish one with a different id. Mounted
 * once by MaintenanceGate, which also hands it the live flag, so it covers
 * every page.
 */
export const UserNotice = ({ flag }: { flag: LiveFlag | null }) => {
  const notice = useMemo(
    () => (flag?.enabled ? parseUserNotice(flag.value) : null),
    [flag]
  );
  const isActive = useIsUserNoticeActive(notice);

  // Hidden until the acknowledgement is read on the client: localStorage is
  // not available during server render, and a hidden default also avoids a
  // flash for users who already acknowledged.
  const [isAcknowledged, setIsAcknowledged] = useState(true);
  const noticeId = notice?.id;
  useEffect(() => {
    if (!noticeId) return;
    setIsAcknowledged(isUserNoticeAcknowledged(noticeId));
  }, [noticeId]);

  const acknowledge = () => {
    if (noticeId) acknowledgeUserNotice(noticeId);
    setIsAcknowledged(true);
  };

  const isVisible = !!notice && isActive && !isAcknowledged;

  return (
    <>
      <UserNoticeModal
        notice={notice}
        open={isVisible && notice.type === "modal"}
        onAcknowledge={acknowledge}
      />
      {isVisible && notice.type === "banner" && (
        <UserNoticeBanner notice={notice} onAcknowledge={acknowledge} />
      )}
    </>
  );
};
